// Attention Field item — answers who · why · what next, with the evidence behind it.
import type { AttentionItem } from '@vero/intelligence';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Avatar, Modal, reasonText } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { telLink, waLink, smsLink, mailLink } from '../core/links.ts';
import { openers } from './forms.tsx';
import { MeetingPrepModal } from './focus.tsx';

export function performAction(rt: Runtime, it: AttentionItem) {
  const a = it.action;
  const o = openers(rt);
  // "Prepare meeting" means the prep sheet, never completing the appointment
  if (it.actionKey === 'attention.action.prepare_meeting' && it.personId) { rt.open(<MeetingPrepModal personId={it.personId} />); return; }
  if (it.rule === 'sync_conflict') { navigate('/sync'); return; }
  switch (a.kind) {
    case 'complete_task': { const t = rt.view.get('tasks', a.taskId); if (t) o.complete(t); return; }
    case 'open_case': navigate(`/cases/${a.caseId}`); return;
    case 'schedule_next': if (a.caseId) navigate(`/cases/${a.caseId}`); else o.followup(a.personId ?? null); return;
    case 'review': o.review(a.personId); return;
    case 'wish': rt.open(<WishModal personId={a.personId} occasion={a.occasion} />); return;
    case 'call': {
      if (a.prospectId) { const p = rt.view.get('prospects', a.prospectId); navigate(`/prospects/${p?.listId ?? ''}?focus=${a.prospectId}`); return; }
      if (a.phone) window.location.href = telLink(a.phone)!;
      if (a.personId) o.followup(a.personId, { kind: 'call', title: rt.t('attention.action.call') });
      return;
    }
    case 'open_person': if (a.personId) navigate(personPath(a.personId, a.tab)); return;
  }
}

export function WishModal({ personId, occasion }: { personId: string; occasion: 'birthday' | 'nameday' }) {
  const rt = useRuntime();
  const p = rt.view.get('persons', personId);
  if (!p) return null;
  const first = p.firstName || p.displayName.split(' ')[0] || '';
  const msg = rt.t(occasion === 'birthday' ? 'template.birthday' : 'template.nameday', { first, signature: rt.advisor.signature || rt.advisor.name || '' });
  return (
    <Modal title={`${rt.t('attention.action.send_wishes')} · ${p.displayName}`} footer={<button className="btn btn-primary" onClick={rt.close}>{rt.t('common.done')}</button>}>
      <div className="stack">
        <textarea className="textarea" readOnly value={msg} rows={3} />
        <div className="row wrap">
          {p.primaryPhone && <a className="btn" href={waLink(p.primaryPhone, msg)} target="_blank" rel="noreferrer"><Icon name="chat" />{rt.t('common.whatsapp')}</a>}
          {p.primaryPhone && <a className="btn" href={smsLink(p.primaryPhone, msg)}><Icon name="chat" />{rt.t('common.sms')}</a>}
          {p.primaryEmail && <a className="btn" href={mailLink(p.primaryEmail, msg.slice(0, 40), msg)}><Icon name="mail" />{rt.t('common.email')}</a>}
          {p.primaryPhone && <a className="btn" href={telLink(p.primaryPhone)}><Icon name="phone" />{rt.t('common.call')}</a>}
        </div>
        <p className="muted small" style={{ margin: 0 }}>{rt.t('appointment.draft_note')}</p>
      </div>
    </Modal>
  );
}

export const RULE_ICON: Record<string, string> = {
  task_overdue: 'followup', task_today: 'task', appointment_today: 'appointment', appointment_tomorrow: 'appointment', case_stalled: 'cases', case_missing: 'document',
  case_no_next: 'cases', review_overdue: 'review', review_never: 'review', review_due_soon: 'review', renewal_soon: 'policy', birthday: 'cake', nameday: 'cake',
  child_milestone: 'household', protection_gap: 'shield', no_next_step: 'followup', silence: 'chat', prospect_followup: 'prospects', data_quality: 'records', hot: 'flame',
  promise_overdue: 'star', waiting_client: 'followup', quote_expiring: 'document', decision_pending: 'compare', needs_missing: 'radar', claim_followup: 'shield', service_open: 'shield', referral_thanks: 'people',
  policy_lapsed: 'policy', life_event: 'spark', loan_maturity: 'data', education_milestone: 'household', mortgage_gap: 'shield', payment_late: 'policy', extraction_review: 'document', sync_conflict: 'data', insured_due: 'shield', premium_overdue: 'policy', kyc_review: 'lock',
};

export function AttentionCard({ items, compact }: { items: AttentionItem[]; compact?: boolean }) {
  const rt = useRuntime();
  const lead = items[0]!;
  const person = lead.personId ? rt.view.get('persons', lead.personId) : undefined;
  const prospect = lead.prospectId ? rt.view.get('prospects', lead.prospectId) : undefined;
  const name = person?.displayName ?? prospect?.name ?? '—';
  const open = () => { if (person) navigate(personPath(person.id)); else performAction(rt, lead); };
  return (
    <article className="att" data-severity={lead.severity} data-testid="attention-item">
      <Avatar name={name} ring={lead.severity} />
      <div style={{ minWidth: 0 }}>
        {!compact && (
          <div className="att-who">
            <b className="person-name" onClick={open} role="link" tabIndex={0}>{name}</b>
            <span className={`chip ${lead.severity === 'critical' ? 'crit' : lead.severity === 'high' ? 'high' : lead.severity === 'medium' ? 'gold' : 'low'}`}>{rt.i18n.enumLabel('severity', lead.severity)}</span>
            {prospect && <span className="chip">{rt.i18n.enumLabel('stage', 'prospect')}</span>}
          </div>
        )}
        <p className="att-reason"><Icon name={RULE_ICON[lead.rule] ?? 'spark'} size={14} /> {reasonText(rt.i18n, lead)}</p>
        {items.length > 1 && (
          <div className="att-more">
            {items.slice(1, 4).map((x) => <span key={x.id} className="chip" title={reasonText(rt.i18n, x)}><Icon name={RULE_ICON[x.rule] ?? 'spark'} size={12} />{shorten(reasonText(rt.i18n, x))}</span>)}
            {items.length > 4 && <span className="chip">+{items.length - 4}</span>}
          </div>
        )}
      </div>
      <div className="att-actions">
        <button className="btn btn-primary btn-sm" onClick={() => performAction(rt, lead)}>{rt.t(lead.actionKey)}</button>
        <button className="btn btn-ghost btn-sm" onClick={() => rt.snooze(lead.id)} title={rt.t('attention.dismiss')}>{rt.t('attention.dismiss')}</button>
      </div>
    </article>
  );
}

function shorten(s: string, n = 46) { return s.length > n ? s.slice(0, n - 1) + '…' : s; }
